import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { isEmpty, isEqual } from "lodash";
import moment from "moment";
import { connect } from "react-redux";
import uniqid from "uniqid";
import { createStructuredSelector } from "reselect";

import Field from "components/Field";
import globalStyles from "components/globalStyles.module.scss";
import Button from "components/Button";
import Drawer from "components/Drawer";
import InputRow from "components/Input/Row";
import MultiSelect from "components/Input/MultiSelect";
import Input from "components/Input";
import Select from "components/Input/Select";
import Checkbox from "components/Input/Checkbox";
import CostTable from "./CostTable";

import { required } from "utils/fieldValidation";
import { resolveObj, useStateSelector } from "utils/field";
import {
    getComponentOptions,
    getSubComponentOptions,
    upgradeOptionsComponents,
    upgradeOptionsSystems,
    stripWindowSkylights,
    stripCeilings,
    getUpgradeEnvelopeRowLabels,
    getUpgradeHvacRowLabels,
    getUpgradeDhwRowLabels,
    getUpgradeOtherRowLabels,
    getUpgradeFinancingRowsLabels,
    componentUpgradeContent,
} from "utils/upgrades";
import { getUpgradeToAdd as getCommUpgradeToAdd } from "utils/communities";
import { getUpgradeToAdd } from "utils/parametric";
import { findAllCodeRefs } from "utils/enclosure";
import { extractPrebuiltEcmCodes, windowsDefaultCode } from "utils/codes";
import { COMBINED_H2K_ECM_SUB_TYPES, compositeSubtypeShouldInheritLabel } from "utils/packageUpgradeEcmCategories";

import { actions } from "../_ducks";
import { actions as commUpgradesActions } from "features/Community/Upgrades/_ducks";
import { updateUpgrade, selectUpgrade, deleteUpgrade } from "store/upgradeLibrary/actions";
import { addUpgradeToParametricAnalysis, updateEcmInputMapUpgrade } from "store/parametricAnalysis/actions";
import { addUpgradeToCommunity, updateCommunityEcmInputMapUpgrade } from "store/community/actions";
import { updatePriceStateParametric } from "store/parametricAnalysis/thunk";
import { initForm, resetForm, removeFormErrors } from "store/form/actions";
import { change } from "store/root/actions";

import classes from "./style.module.scss";

const { updatePackageUpgrades, deletePackageUpgrade, updateUpgradeCost } = actions;
const { updateCommPackageUpgrades, deleteCommPackageUpgrade, updateCommUpgradeCost } = commUpgradesActions;
const { adjustCommPackageCost } = commUpgradesActions;

const UpgradeDrawer = React.memo(
    ({
        isOpen = false,
        toggleOpen,
        selectedUpgradeType: upgradeType, //This will come in as "spaceHeating", for example
        selectedUpgradeH2kType: upgradeH2kType = upgradeType,
        selectedPackage,
        selectedUpgrade,
        submitting,
        // invalid,
        reset,
        resetState,
        change,
        isNew,
        showCost,
        toggleShowCost,
        isParametricAnalysis = false,
        isUpgradeLibrary = false,
        isCommunity = false,
        selectedUpgradeLabel,
        initUpgradeData = {},
        autoGenerateLabelFromParametric = true,
        initForm,
        resetForm,
        deleteUpgrade,
        deleteCommUpgrade,
        deleteUpgradeFromLib,
        selectUpgrade,
        adjustCommPackageCost,
        analysisId,
        removeFormErrors,
        upgradeWhereToAddCode,
        modelsDefaultCodes,
        canBeEditedFromParametric = true,
        canBeEditedFromCommunity = true,
    }) => {
        const dispatch = useDispatch();
        const [isClicked, setIsClicked] = useState(false);

        const clearUpgradeDrawerErrors = () => {
            // Clear all errors for the upgrade drawer, without this, we can unintentionally lock-out the save button.
            removeFormErrors("newForm.upgradeDrawer"); // label, selectedComponents, nested fields
        };

        // Form Data
        const form = useStateSelector("newForm.upgradeDrawer") || {};
        const formToCompare = useStateSelector("newForm.upgradeDrawerToCompare") || {};

        const { selectedComponents: selectedComponentsField = [], autoGenerateLabel } = form;

        // State Values
        const { codeLib = {} } = useStateSelector("user");
        const { packages = {} } = useStateSelector("upgrades");
        const { packages: commPackages = {}, baseModelId = null, capitalCosts = {} } = useStateSelector("commUpgrades");
        const { currentPackage = {} } = useStateSelector("upgradeLibrary");

        // Model
        const { modelData: modelDataFromModel = {} } = useStateSelector("model") || {};

        // Parametric Analysis
        const { parametricAnalysis = {} } = useStateSelector("parametricAnalysis") || {};
        const { model: { upgrades: upgradesFromParametric = {}, modelData: modelDataFromParametric = {} } = {} } =
            parametricAnalysis || {};

        // Community
        const { community = {} } = useStateSelector("community") || {};
        const { modelData: { models: commModels = {} } = {} } = community || {};
        const modelDataFromCommunity = commModels[baseModelId] ? commModels[baseModelId] : {};
        const communityModelIds = Object.keys(commModels) || [];

        const ecmInputMap = parametricAnalysis?.ecmInputMap || community?.ecmInputMap || {};

        // Model Data
        const {
            components = {},
            airTightness = {},
            codes = {},
            heatingCooling = {},
            ventilation = {},
            domesticHotWater = {},
            generation,
            transportation,
            program,
            baseLoads = {},
        } = isParametricAnalysis ? modelDataFromParametric : isCommunity ? modelDataFromCommunity : modelDataFromModel;

        //  Upgrade Package
        const { upgrades: packageUpgrades = {} } = isUpgradeLibrary
            ? currentPackage
            : isParametricAnalysis
              ? upgradesFromParametric
              : isCommunity
                ? commPackages[selectedPackage] || {}
                : packages[selectedPackage] || {};

        const { created } = packageUpgrades[selectedUpgrade] || {};

        // Ecm Input Map
        const upgradeFromEcmInputMap = ecmInputMap?.[upgradeType]?.[selectedUpgrade] || {};

        // Find available enclosure components for upgrade
        let availableComponents = ["window", "door", "floorHeader"].includes(upgradeH2kType)
            ? getSubComponentOptions(upgradeH2kType, components, packageUpgrades, selectedUpgrade)
            : getComponentOptions(upgradeH2kType, components, packageUpgrades, selectedUpgrade);

        if (upgradeType === "window" || upgradeType === "skylights") {
            availableComponents = stripWindowSkylights(availableComponents, upgradeType);
        } else if (upgradeType === "ceilingWithAttic" || upgradeType === "cathedral") {
            availableComponents = stripCeilings(availableComponents, components, upgradeType);
        }

        const unavailableSystems = Object.keys(packageUpgrades)
            .filter((key) => {
                const { upgradeH2kType } = packageUpgrades[key];
                return upgradeOptionsSystems.find(({ value }) => value === upgradeH2kType);
            })
            .map((key) => packageUpgrades[key].upgradeH2kType);

        // need to enable local state change upon selecting enclosure components
        const selectedComponents =
            selectedComponentsField.constructor === Array ? selectedComponentsField : [selectedComponentsField];

        const isEnvelopeComponent = upgradeOptionsComponents.find(({ value = "" }) => value === upgradeH2kType);
        const invalid =
            !isParametricAnalysis &&
            !isUpgradeLibrary &&
            !isCommunity &&
            isEnvelopeComponent &&
            required(selectedComponents);

        // Initialize drawer with any existing values
        const isUpgradeEdit = selectedUpgrade && !isNew;
        const canBeRemoved = isUpgradeEdit && !isParametricAnalysis && canBeEditedFromCommunity;

        const getInitialValues = () => {
            // Base initial values
            const baseValues = {
                label: { [upgradeType]: selectedUpgradeLabel },
                autoGenerateLabel: isParametricAnalysis ? autoGenerateLabelFromParametric : true,
                ...((selectedPackage && packageUpgrades[selectedUpgrade]) || initUpgradeData), //Fallback order packageUpgrades > initUpgradeData
            };

            // Early return for non-parametric analysis or non-community
            if (!(isParametricAnalysis || isCommunity) || !selectedUpgrade) {
                return baseValues;
            }

            // Get upgrade data based on priority: ecmInputMap > packageUpgrades > initUpgradeData
            const upgradeData =
                (!isEmpty(upgradeFromEcmInputMap) && upgradeFromEcmInputMap.packageValues) ||
                (selectedPackage &&
                    !isEmpty(packageUpgrades?.[selectedUpgrade] || {}) &&
                    packageUpgrades[selectedUpgrade]) ||
                initUpgradeData;

            return {
                ...baseValues,
                ...upgradeData,
            };
        };

        let initialValues = getInitialValues();

        if (
            !Object.keys(initialValues).includes("fields") &&
            (isUpgradeLibrary || isParametricAnalysis || isCommunity)
        ) {
            initialValues = {
                ...initialValues,
                ...initUpgradeData,
            };
        }

        const { label: typeLabel = "", singleSelect = false } =
            upgradeOptionsComponents.find(({ value }) => value === upgradeH2kType) || {};

        const UpgradeComponent = componentUpgradeContent(upgradeType);

        /* Button functions */
        const handleClose = () => {
            setIsClicked(false);
            toggleOpen({});

            if (isNew && isUpgradeLibrary) deleteUpgradeFromLib(selectedUpgrade);

            selectUpgrade(null);

            if (resetState) {
                // Removes entire upgrade from ecmInputMap if 'New Upgrade' is cancelled after adding new code
                // Incomplete temporary upgrade objects like { label="", codes={...} } are discarded
                if (isNew) {
                    resetState();
                }
                // Removes newly created codes from upgrade if 'Edit' is cancelled
                // Incomplete upgrade objects like { label="", codes={...} } may occur if table upgrades are missing from the ecmInputMap
                else {
                    const { ecmInputMapUpgradeType, ecmInputMapUpgradeId, ecmInputMapUpgrade } = upgradeWhereToAddCode;
                    if (isCommunity) {
                        dispatch(
                            updateCommunityEcmInputMapUpgrade(ecmInputMapUpgradeType, ecmInputMapUpgradeId, {
                                ...ecmInputMapUpgrade,
                                codes: initialValues?.codes || {},
                            })
                        );
                    }
                    // TODO: Xaria add whereToAddCode to toggleUpgrade in Parametric Analysis
                    // else if (isParametricAnalysis) {
                    //     dispatch(
                    //         updateEcmInputMapUpgrade(ecmInputMapUpgradeType, ecmInputMapUpgradeId, {
                    //             ...ecmInputMapUpgrade,
                    //             codes: initialValues?.codes || {},
                    //         })
                    //     );
                    // }
                }
            }

            clearUpgradeDrawerErrors();
            resetForm("upgradeDrawer");
            resetForm("upgradeDrawerToCompare");
        };

        const handleDelete = () => {
            /* Delete from Upgrade Library */
            if (isUpgradeLibrary) {
                deleteUpgradeFromLib(selectedUpgrade);
            }

            /* Delete from Community */
            if (isCommunity) {
                // Deletes this package upgrade from the community's upgrades state (Populates upgrades table)
                deleteCommUpgrade({
                    packageId: selectedPackage,
                    upgradeId: selectedUpgrade,
                });

                // Subtracts upgrade cost from this package for each base community model (Updates capital costs)
                let { cost = {} } = form || {};
                const upgradeTotals = cost[upgradeType]?.communityTotals || {};

                communityModelIds.forEach((modelId) => {
                    const upgradeCost = upgradeTotals?.[modelId] || 0;
                    const totalPackageCost = capitalCosts[modelId]?.[selectedPackage] || 0;

                    adjustCommPackageCost({
                        modelId,
                        packageId: selectedPackage,
                        amount: totalPackageCost - upgradeCost,
                    });
                });
            }

            /* Delete from Model */
            if (!isUpgradeLibrary && !isCommunity && !isParametricAnalysis) {
                deleteUpgrade({
                    packageId: selectedPackage,
                    upgradeId: selectedUpgrade,
                });
            }

            /* Remove package reference from every selected component */
            if (!isEmpty(components)) {
                selectedComponents.map((path) => {
                    const componentData = resolveObj(path, components);
                    const upgradePackages = componentData?.upgradePackages;
                    const updatedPackages =
                        upgradePackages && typeof upgradePackages === "object"
                            ? Object.entries(upgradePackages).reduce((acc, [curKey, curVal]) => {
                                  if (curKey !== selectedPackage) {
                                      return { ...acc, [curKey]: curVal };
                                  }
                                  return acc;
                              }, {})
                            : {};

                    const componentModelPath = `model.modelData.components.${path}.upgradePackages`;
                    change(componentModelPath, updatedPackages);
                });
            }

            /* Clear drawer data and close*/
            clearUpgradeDrawerErrors();
            toggleOpen({});
            if (reset) {
                resetForm("upgradeDrawer");
                resetForm("upgradeDrawerToCompare");
            }

            // Remove upgrade from ecmInputMap (Community/Parametric Analysis)
            // if (resetState) resetState();
        };

        const onSubmit = async () => {
            const upgradeId = selectedUpgrade || uniqid("upgrade-");
            const dateTimeFormatted = moment().format("LLLL");
            const newlyCreated = { created: dateTimeFormatted };

            // if (!isParametricAnalysis) {
            //     upgradeH2kType = upgradeType;
            // }

            let {
                selectedComponents: selectedComponentsField = [],
                fields = {},
                cost = {},
                label: { [upgradeType]: label = "N/A" } = {},
                label: initLabels = {},
                autoGenerateLabel,
                codes: upgradeCodes = {},
            } = form || {};

            let { selectedComponents: oldSelectedComponents = [] } = formToCompare || {};

            if (upgradeType === "grant" || upgradeType === "loan") {
                const fieldsArr = Object.entries(fields).reduce((acc, field) => {
                    if (field[1]?.amount !== null) {
                        return {
                            ...acc,
                            [`${field[0]}`]: field[1],
                        };
                    } else {
                        return acc;
                    }
                }, {});

                fields = fieldsArr;
            }

            let codesToAdd = {};

            if (isUpgradeLibrary || isParametricAnalysis || isCommunity) {
                const fieldsKeys = Object.keys(fields);

                const codeLibKeys = Object.keys(codeLib);

                let allCodes = { ...codes };

                codeLibKeys
                    .map((key) => Object.keys(codeLib[key].codes).map((k) => ({ [k]: codeLib[key].codes[k] })))
                    .flat()
                    .forEach((code) => {
                        allCodes = { ...allCodes, ...code };
                    });
                // .reduce((obj, item) => Object.assign(allCodes, obj), {});

                // Include prebuilt ECM codes for community upgrades
                if (isCommunity) {
                    const prebuiltEcmCodes = extractPrebuiltEcmCodes(upgradeType);
                    allCodes = { ...allCodes, ...windowsDefaultCode, ...prebuiltEcmCodes };
                }

                // Fetch codes present in the upgrade fields to be added to the ecmInputMap
                if (fieldsKeys.includes("wall") || fieldsKeys.includes("floor")) {
                    const codeRefValues = findAllCodeRefs(fields).filter((val) => val !== "UserSpecified");

                    codeRefValues.forEach((codeRef) => {
                        if (allCodes[codeRef]) {
                            codesToAdd = {
                                ...codesToAdd,
                                [codeRef]: {
                                    ...allCodes[codeRef],
                                    codeRef: codeRef,
                                },
                            };
                        }
                    });
                } else {
                    for (let i = 0; i < fieldsKeys.length; i++) {
                        const key = fieldsKeys[i];

                        // Skip fields without a codeRef
                        if (!fields[key]?.codeRef) continue;

                        // Add code to the ecmInputMap if it exists in the allCodes object
                        if (allCodes[fields[key].codeRef]) {
                            codesToAdd = {
                                ...codesToAdd,
                                [fields[key].codeRef]: {
                                    ...allCodes[fields[key].codeRef],
                                    codeRef: fields[key].codeRef,
                                },
                            };
                        }
                    }
                }
            }

            //Upgrade type could be "spaceHeating", "skylights", etc, while we need it to be the h2k upgrade here ("heatingCooling", "window")

            let initUpgrade = {
                upgradeType: upgradeH2kType,
                upgradeSubType: upgradeType,
                selectedComponents,
                fields,
                cost,
                codes: upgradeCodes ? { ...upgradeCodes, ...codesToAdd } : codesToAdd ? { ...codesToAdd } : {},
                autoGenerateLabel,
                ...(created ? { created } : newlyCreated),
            };

            //must do this because we use the presence of the key "skylights" in label to differentiate between regular windows and skylights
            if (upgradeType === "skylights") {
                initUpgrade = {
                    ...initUpgrade,
                    label: { skylights: "None" },
                };
            }

            // Generate label for upgrade
            const isEnvelopeComponent = upgradeOptionsComponents.find(({ value = "" }) => value === upgradeH2kType);
            const isProgramUpgrade = ["reducedOperating"].includes(upgradeType);
            let generatedLabel = "Error Generating Label";

            if (isEnvelopeComponent || upgradeH2kType === "airTightness") {
                generatedLabel =
                    getUpgradeEnvelopeRowLabels({
                        components: isCommunity ? {} : components,
                        codes,
                        airtightness: airTightness,
                        rowId: upgradeType,
                        upgradePackage: {
                            [selectedPackage]: {
                                upgrades: {
                                    [upgradeId]: initUpgrade,
                                },
                            },
                        },
                        ecmInputMap,
                    })?.label || "Error Generating Label";
            } else if (["heatingCooling", "ventilation"].includes(upgradeH2kType)) {
                generatedLabel =
                    getUpgradeHvacRowLabels({
                        heatingCooling,
                        ventilation,
                        rowId: upgradeType,
                        upgradePackage: {
                            [selectedPackage]: {
                                upgrades: {
                                    [upgradeId]: initUpgrade,
                                },
                            },
                        },
                    })?.label || "Error Generating Label";
            } else if (["domesticHotWater"].includes(upgradeH2kType)) {
                generatedLabel =
                    getUpgradeDhwRowLabels({
                        domesticHotWater,
                        heatingCooling,
                        rowId: upgradeType,
                        upgradePackage: {
                            [selectedPackage]: {
                                upgrades: {
                                    [upgradeId]: initUpgrade,
                                },
                            },
                        },
                    })?.label || "Error Generating Label";
            } else if (["generation", "transportation", "baseLoads", "reducedOperating"].includes(upgradeH2kType)) {
                generatedLabel =
                    getUpgradeOtherRowLabels({
                        generation,
                        transportation,
                        program,
                        baseLoads,
                        rowId: upgradeType,
                        upgradePackage: {
                            [selectedPackage]: {
                                upgrades: {
                                    [upgradeId]: initUpgrade,
                                },
                            },
                        },
                    })?.label || "Error Generating Label";
            } else if (["loan", "grant", "financialValuation"].includes(upgradeH2kType)) {
                generatedLabel =
                    getUpgradeFinancingRowsLabels({
                        rowId: upgradeType,
                        upgradePackage: {
                            [selectedPackage]: {
                                upgrades: {
                                    [upgradeId]: initUpgrade,
                                },
                            },
                        },
                    })?.label || "Error Generating Label";
            }

            const setLabel = autoGenerateLabel ? generatedLabel : label;

            // One saved upgrade can back multiple ECM/table rows (e.g. heatingCooling → spaceHeating + spaceCooling).
            // The form often only binds `label.${upgradeType}`; spreading `initLabels` can duplicate the active row's
            // value onto sibling keys. Keep sibling labels from the persisted package and only update this row.
            const persistedRowLabels =
                packageUpgrades[selectedUpgrade]?.label && typeof packageUpgrades[selectedUpgrade].label === "object"
                    ? packageUpgrades[selectedUpgrade].label
                    : null;
            const isCombinedH2kUpgrade = !!COMBINED_H2K_ECM_SUB_TYPES[upgradeH2kType];
            const usePersistedSiblingLabels =
                isCommunity && isCombinedH2kUpgrade && isUpgradeEdit && persistedRowLabels;

            let upgrade = {
                ...initUpgrade,
                label: usePersistedSiblingLabels
                    ? {
                          ...persistedRowLabels,
                          [upgradeType]: setLabel,
                      }
                    : {
                          ...initLabels,
                          [upgradeType]: setLabel,
                      },
            };

            const compositeSubs = COMBINED_H2K_ECM_SUB_TYPES[upgradeH2kType];
            if ((isCommunity || isParametricAnalysis) && compositeSubs?.length) {
                const lbl =
                    upgrade.label && typeof upgrade.label === "object" && !Array.isArray(upgrade.label)
                        ? { ...upgrade.label }
                        : {};
                for (const key of compositeSubs) {
                    if (!compositeSubtypeShouldInheritLabel(upgradeH2kType, key, fields, cost)) continue;
                    const cur = lbl[key];
                    if (cur == null || cur === "" || cur === "None") {
                        lbl[key] = setLabel;
                    }
                }
                upgrade = { ...upgrade, label: lbl };
            }

            if (isUpgradeLibrary) {
                dispatch(updateUpgrade(upgradeId, upgrade));
            }

            if (isParametricAnalysis) {
                delete upgrade.upgradeType;
                delete upgrade.selectedComponents;
                // upgradeKey, upgradeSubType, upgradeType, parentUpgradeKey
                const upgradeToAdd = isNew
                    ? getUpgradeToAdd(
                          upgrade,
                          {
                              upgradeKey: upgradeId,
                              upgradeSubType: upgradeType,
                              upgradeType: upgradeH2kType,
                              parentUpgradeKey: null,
                          },
                          ecmInputMap,
                          0,
                          true
                      )
                    : null;

                if (isNew) {
                    dispatch(addUpgradeToParametricAnalysis(upgradeType, upgradeToAdd));
                } else {
                    const { cost: { [upgradeType]: { total: newCost = 0 } = {} } = {} } = upgrade;
                    const {
                        packageValues: {
                            cost: { [upgradeType]: { total: oldCost = 0 } = {} } = {},
                            fields: oldFields = {},
                        } = {},
                        firstBatchId = null,
                    } = ecmInputMap[upgradeType][upgradeId];

                    if (newCost !== oldCost && firstBatchId) {
                        await dispatch(updatePriceStateParametric(analysisId));
                    }

                    let areNewPackageValues = !isEqual(fields, oldFields);

                    await dispatch(
                        updateEcmInputMapUpgrade(
                            upgradeType,
                            upgradeId,
                            { ...upgrade, upgradeSubType: upgradeType },
                            areNewPackageValues
                        )
                    );
                    const parametricCompositeSubs = COMBINED_H2K_ECM_SUB_TYPES[upgradeH2kType];
                    if (parametricCompositeSubs?.length) {
                        for (const sub of parametricCompositeSubs) {
                            if (sub === upgradeType) continue;
                            if (!ecmInputMap?.[sub]?.[upgradeId]) continue;
                            await dispatch(
                                updateEcmInputMapUpgrade(
                                    sub,
                                    upgradeId,
                                    { ...upgrade, upgradeSubType: sub },
                                    areNewPackageValues
                                )
                            );
                        }
                    }
                }

                // if (resetState) {
                //     delete resetState.removeUpgradeFromParametricAnalysis;

                //     resetState();
                // }
            }

            if (isCommunity) {
                // upgradeType, selectedComponents, upgradeKey, upgradeSubType, upgradeType, parentUpgradeKey
                const upgradeToAdd = isNew
                    ? getCommUpgradeToAdd(
                          upgrade,
                          {
                              upgradeKey: upgradeId,
                              upgradeSubType: upgradeType,
                              upgradeType: upgradeH2kType,
                              parentUpgradeKey: null,
                              upgradePackageId: selectedPackage, // Match upgradePackageId to community upgrade
                          },
                          ecmInputMap,
                          0,
                          true
                      )
                    : null;

                // Add upgrade to the community's ecmInputMap (For running simulations)
                if (isNew) {
                    await dispatch(addUpgradeToCommunity(upgradeType, upgradeToAdd));
                } else {
                    // Update upgrade in the community's ecmInputMap (For running simulations)
                    const payloadBase = {
                        ...upgrade,
                        upgradePackageId: selectedPackage,
                    };
                    await dispatch(
                        updateCommunityEcmInputMapUpgrade(upgradeType, upgradeId, {
                            ...payloadBase,
                            upgradeSubType: upgradeType,
                        })
                    );
                    const communityCompositeSubs = COMBINED_H2K_ECM_SUB_TYPES[upgradeH2kType];
                    if (communityCompositeSubs?.length) {
                        for (const sub of communityCompositeSubs) {
                            if (sub === upgradeType) continue;
                            if (!ecmInputMap?.[sub]?.[upgradeId]) continue;
                            await dispatch(
                                updateCommunityEcmInputMapUpgrade(sub, upgradeId, {
                                    ...payloadBase,
                                    upgradeSubType: sub,
                                })
                            );
                        }
                    }
                }

                // Reusing upgradeId, no longer needed
                // TODO: Xaria Double check upgradeWhereToAddCode is clearing after creation
                // if (resetState && isNew) {
                //     delete resetState.removeUpgradeFromCommunity;
                //     resetState();
                // }

                // Add upgrade to package in Community's upgrades state for Upgrades table
                const commUpgradeId = isNew ? Object.keys(upgradeToAdd)[0] : upgradeId;
                await dispatch(
                    updateCommPackageUpgrades({
                        packageId: selectedPackage,
                        dateTime: dateTimeFormatted,
                        upgrades: { [commUpgradeId]: upgrade },
                    })
                );
                // Add calculated cost to community upgrade state
                await dispatch(
                    updateCommUpgradeCost({
                        packageId: selectedPackage,
                        upgradeId: commUpgradeId,
                        upgradeType: upgradeType,
                        total: cost[upgradeType]?.total || 0,
                        communityTotals: cost[upgradeType]?.communityTotals || {},
                        items: cost[upgradeType]?.items || {},
                        communityItems: cost[upgradeType]?.communityItems || {},
                    })
                );

                // Recalculate package cost for each base community model (Updates capital costs)
                communityModelIds.forEach((modelId) => {
                    const packageTotal = Object.entries({ ...packageUpgrades, [commUpgradeId]: upgrade }).reduce(
                        (acc, [upgradeId, packageUpgrade]) => {
                            const { cost = {}, upgradeType = "", upgradeSubType = "" } = packageUpgrade;
                            const itemTotal = cost[upgradeSubType || upgradeType]?.communityTotals?.[modelId] || 0;
                            return acc + itemTotal;
                        },
                        0
                    );

                    adjustCommPackageCost({
                        modelId,
                        packageId: selectedPackage,
                        amount: packageTotal,
                    });
                });
            }

            if (!isUpgradeLibrary && !isParametricAnalysis && !isCommunity) {
                // Add upgrade only if it's a system component or an envelope component with selected components defined
                if (!isEnvelopeComponent || !isEmpty(selectedComponents)) {
                    await dispatch(
                        updatePackageUpgrades({
                            packageId: selectedPackage,
                            dateTime: dateTimeFormatted,
                            upgrades: { [upgradeId]: upgrade },
                        })
                    );
                    await dispatch(
                        updateUpgradeCost({
                            packageId: selectedPackage,
                            upgradeId: upgradeId,
                            upgradeType: upgradeType,
                            cost: cost[upgradeType]?.total || 0,
                            items: cost[upgradeType]?.items || {},
                        })
                    );
                }

                // Update in Model
                const componentReference = {
                    upgradeId,
                };

                if (isEnvelopeComponent) {
                    // Add upgradeId reference to envelope component
                    if (!isEmpty(selectedComponents)) {
                        // Add package with upgrade reference to component if it's now selected
                        const newlySelectedComponents = selectedComponents.filter(
                            (id) => !oldSelectedComponents.includes(id)
                        );

                        newlySelectedComponents.map((path) => {
                            const componentModelPath = `model.modelData.components.${path}.upgradePackages.${selectedPackage}`;
                            change(componentModelPath, componentReference);
                        });

                        // Remove package reference from component if no longer selected
                        const unselectedComponents = oldSelectedComponents.filter(
                            (id) => !selectedComponents.includes(id)
                        );

                        unselectedComponents.map((path) => {
                            const componentData = resolveObj(path, components);
                            const upgradePackages = componentData?.upgradePackages;

                            const updatedPackages =
                                upgradePackages && typeof upgradePackages === "object"
                                    ? Object.entries(upgradePackages).reduce((acc, [curKey, curVal]) => {
                                          if (curKey !== selectedPackage) {
                                              return { ...acc, [curKey]: curVal };
                                          }
                                          return acc;
                                      }, {})
                                    : {};

                            const componentModelPath = `model.modelData.components.${path}.upgradePackages`;
                            change(componentModelPath, updatedPackages);
                        });
                    } else if (isUpgradeEdit) {
                        // Remove upgrade if selected components is left empty after an edit
                        deleteUpgrade({
                            packageId: selectedPackage,
                            upgradeId: selectedUpgrade,
                        });
                    }
                } else if (isProgramUpgrade) {
                    const programPath = `model.modelData.program.options.${upgradeType}.upgradePackages.${selectedPackage}`;
                    change(programPath, componentReference);
                } else {
                    // Add upgradeId reference to System component
                    const systemPath = `model.modelData.${upgradeH2kType}.upgradePackages.${selectedPackage}`;
                    change(systemPath, componentReference);
                }
            }

            toggleOpen({});
            selectUpgrade(null);
            clearUpgradeDrawerErrors();
            resetForm("upgradeDrawer");
            resetForm("upgradeDrawerToCompare");
        };

        useEffect(() => {
            initForm("upgradeDrawer", initialValues);
            initForm("upgradeDrawerToCompare", initialValues);
        }, [upgradeType]);

        // Upgrade library type dropdowns read `model.modelData.codes`
        //  Keep that scratch pad scoped to the open upgrade so codes created in another
        // package do not show up as empty options.
        useEffect(() => {
            if (!isUpgradeLibrary) return;
            change("model.modelData.codes", isOpen ? packageUpgrades[selectedUpgrade]?.codes || {} : {});
        }, [isOpen, selectedUpgrade, selectedPackage, isUpgradeLibrary]);

        useEffect(() => {
            if (autoGenerateLabel) {
                removeFormErrors(`newForm.upgradeDrawer.label.${upgradeType}`);
            }
        }, [autoGenerateLabel, upgradeType, removeFormErrors]);

        const codeRefs =
            modelsDefaultCodes?.flatMap?.(([id, data]) => {
                if (!data) return []; // Skip null entries

                return Object.values(data).flatMap((component) => {
                    // We search through the component properties (like wallInsType or windowType)
                    // and extract the codeRef if it exists
                    return Object.values(component)
                        .map((val) => val?.codeRef)
                        .filter((ref) => ref !== undefined);
                });
            }) || [];

        return (
            <Drawer
                open={isOpen}
                title={`${isUpgradeEdit ? "Edit" : "Add New"} ${typeLabel} Upgrade`}
                subtitle={
                    isUpgradeEdit
                        ? `Created ${moment(created).format("MMMM DD, YYYY")}`
                        : `Select the type of upgrade to get started.`
                }
                close={() => {
                    !isClicked ? setIsClicked(true) : handleClose();
                }}
                noCloseButton={true}
                classes={{
                    paperClass: classes.upgradeDrawerPaper,
                }}
                width={1024}
            >
                {isClicked && (
                    <div className={classes.warningPane} onClick={() => setIsClicked(false)}>
                        <div className={classes.warning}>
                            <p>
                                Click off of the drawer twice to <strong>Discard Changes</strong>
                            </p>
                            <p style={{ fontSize: "15px" }}>Click here to continue editing</p>
                        </div>
                    </div>
                )}
                <div className={classes.contentWrapper}>
                    <div className={classes.mainContent}>
                        <InputRow>
                            <Field
                                component={Input}
                                name={`newForm.upgradeDrawer.label.${upgradeType}`}
                                label="Label"
                                validate={autoGenerateLabel ? [] : [required]}
                                disabled={autoGenerateLabel}
                            />
                            <Field
                                component={Checkbox}
                                name={"newForm.upgradeDrawer.autoGenerateLabel"}
                                label="Use automatically generated label"
                                type="checkbox"
                            />
                            {["loan", "grant", "financialValuation"].includes(upgradeType) &&
                                !isUpgradeLibrary &&
                                !isCommunity &&
                                !isParametricAnalysis && (
                                    <div className={classes.eligibleUpgrades}>
                                        <UpgradeComponent
                                            isParametricAnalysis={isParametricAnalysis}
                                            isCommunity={isCommunity}
                                            accessor={"newForm.upgradeDrawer.fields"}
                                            change={change}
                                            formName={"newForm.upgradeDrawer"}
                                            disabledCodeEdit
                                            isUpgrade
                                            selectedPackage={selectedPackage}
                                        />
                                    </div>
                                )}
                            {/* below is checking if the components list contains upgradeType
                            looks weird because it is using the 'some' method */}
                            {upgradeType &&
                                upgradeOptionsComponents.some(({ value }) => value === upgradeType) &&
                                !singleSelect &&
                                selectedComponents &&
                                !isUpgradeLibrary &&
                                !isCommunity &&
                                !isParametricAnalysis && (
                                    <Field
                                        component={MultiSelect}
                                        name={"newForm.upgradeDrawer.selectedComponents"}
                                        label={`${typeLabel} Components`}
                                        options={availableComponents.map(
                                            ({ disabled = false, label = "", ...rest }) => ({
                                                ...rest,
                                                disabled,
                                                label: disabled
                                                    ? `${label} (Upgrade already applied in this package)`
                                                    : label,
                                            })
                                        )}
                                        // input={{
                                        //     value: selectedComponentsField,
                                        //     onChange: (value) =>
                                        //         setUpgradeDrawerData({
                                        //             ...upgradeDrawerData,
                                        //             selectedComponentsField: value,
                                        //         }),
                                        // }}
                                        validate={[required]}
                                        hasSelectAll
                                    />
                                )}
                            {!isUpgradeLibrary &&
                                !isParametricAnalysis &&
                                !isCommunity &&
                                upgradeType &&
                                upgradeOptionsComponents.some(({ value }) => value === upgradeType) &&
                                singleSelect && (
                                    <Field
                                        component={Select}
                                        name={"newForm.upgradeDrawer.selectedComponents"}
                                        label={`${typeLabel} Component`}
                                        options={
                                            availableComponents
                                                ? availableComponents.map(
                                                      ({ disabled = false, label = "", value, ...rest }) => ({
                                                          ...rest,
                                                          disabled,
                                                          value: [value],
                                                          label: disabled
                                                              ? `${label} (Upgrade already applied in this package)`
                                                              : label,
                                                      })
                                                  )
                                                : []
                                        }
                                        validate={[required]}
                                        hasSelectAll
                                    />
                                )}
                            {((upgradeType &&
                                UpgradeComponent &&
                                (upgradeOptionsSystems.some(({ value }) => value === upgradeType) ||
                                    selectedComponents.length > 0)) ||
                                (UpgradeComponent && (isUpgradeLibrary || isCommunity || isParametricAnalysis))) && (
                                <div className={classes.eligibleUpgrades}>
                                    <UpgradeComponent
                                        accessor={"newForm.upgradeDrawer.fields"}
                                        change={change}
                                        formName={"newForm.upgradeDrawer"}
                                        disabledCodeEdit
                                        isUpgrade
                                        isCommunity={isCommunity}
                                        isParametricAnalysis={isParametricAnalysis}
                                        upgradeType={upgradeType}
                                        selectedUpgrade={selectedUpgrade}
                                        selectedPackage={selectedPackage}
                                        isUpgradeLibrary={isUpgradeLibrary}
                                        componentAccessor={
                                            !isUpgradeLibrary && !isCommunity
                                                ? `model.modelData.components.${selectedComponents[0]}`
                                                : {}
                                        }
                                        // codeRefs={codeRefs}
                                        // For foundation components
                                        canBeEditedFromParametric={
                                            canBeEditedFromParametric && canBeEditedFromCommunity
                                        }
                                        // associatedHeatingSystem={associatedHeatingSystem} //For DHW
                                    />
                                </div>
                            )}
                        </InputRow>

                        {!["loan", "grant", "financialValuation"].includes(upgradeType) && (
                            <CostTable
                                className={classes.costTable}
                                selectedComponents={selectedComponents}
                                accessor={`newForm.upgradeDrawer.cost.${upgradeType}`}
                                formName={"newForm.upgradeDrawer"}
                                change={change}
                                selectedPackage={selectedPackage}
                                upgradeType={upgradeType}
                                selectedUpgrade={selectedUpgrade}
                                showCost={showCost}
                                toggleShowCost={toggleShowCost}
                                isUpgradeLibrary={isUpgradeLibrary}
                                isParametricAnalysis={isParametricAnalysis}
                                isCommunity={isCommunity}
                                upgradeH2kType={upgradeH2kType}
                            />
                        )}
                    </div>
                    <div className={classes.drawerButtonWrapper}>
                        <div
                            className={`${globalStyles.buttons}`}
                            style={{
                                marginTop: "0rem",
                                gridTemplateColumns: canBeRemoved ? "12.5rem 12.5rem 12.5rem" : "12.5rem 12.5rem",
                            }}
                        >
                            {canBeRemoved && (
                                <Button large type="red" onClick={handleDelete}>
                                    Remove Upgrade
                                </Button>
                            )}
                            <Button large type="hollow" onClick={handleClose}>
                                Cancel
                            </Button>
                            <Button large onClick={onSubmit} disabled={submitting || invalid}>
                                {submitting
                                    ? `${isUpgradeEdit ? "Editing" : "Adding"} Upgrade...`
                                    : `${isUpgradeEdit ? "Confirm Changes" : "Add Upgrade"}`}
                            </Button>
                        </div>
                    </div>
                </div>
            </Drawer>
        );
    },
    isEqual
);

const mapStateToProps = createStructuredSelector({});

const mapDispatchToProps = (dispatch) => ({
    deleteUpgrade: ({ packageId, upgradeId }) => {
        dispatch(deletePackageUpgrade({ packageId, upgradeId }));
    },
    deleteCommUpgrade: ({ packageId, upgradeId }) => {
        dispatch(deleteCommPackageUpgrade({ packageId, upgradeId }));
    },
    deleteUpgradeFromLib: (upgradeId) => dispatch(deleteUpgrade(upgradeId)),
    selectUpgrade: (upgradeId) => dispatch(selectUpgrade(upgradeId)),
    adjustCommPackageCost: ({ modelId, packageId, amount }) => {
        dispatch(adjustCommPackageCost({ modelId, packageId, amount }));
    },
    initForm: (formId, initialValues) => dispatch(initForm(formId, initialValues)),
    resetForm: (formId) => dispatch(resetForm(formId)),
    change: (path, value) => dispatch(change(path, value)),
    removeFormErrors: (errorName) => dispatch(removeFormErrors(errorName)),
});

export default connect(mapStateToProps, mapDispatchToProps)(UpgradeDrawer);
